import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PreSurrenderGuide } from './data';
import { colors, contentBlockShadow } from '../../../theme';
import { track } from '../analytics';

const AVATAR = '/hub/checklist/fig_avatar.png';

interface NextUpGuideProps {
  /** The next unreviewed guide ({pet} tokens already filled), or null when all are done. */
  guide: PreSurrenderGuide | null;
  petName: string;
  percent: number;
  done: number;
  total: number;
  onGo: () => void;
}

/**
 * Pre-surrender hero: mirrors the checklist NextUpCard, but counts reviewed
 * guides and shows a completion message (not the membership upsell) once every
 * guide is done.
 */
function NextUpGuide({
  guide, petName, percent, done, total, onGo,
}: NextUpGuideProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '12px',
        boxShadow: contentBlockShadow,
        p: { xs: '32px 24px', lg: '24px 40px' },
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 3,
        alignItems: 'center',
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'center',
        textAlign: { xs: 'center', lg: 'left' },
        gap: { xs: 2.5, lg: 3 },
        flex: { xs: 'none', lg: 1 },
        width: { xs: '100%', lg: 'auto' },
        maxWidth: { xs: 400, lg: 'none' },
        minWidth: 0,
      }}
      >
        <Box
          component="img"
          src={AVATAR}
          alt=""
          sx={{
            width: { xs: 100, lg: 137 },
            height: { xs: 100, lg: 137 },
            borderRadius: '50%',
            flexShrink: 0,
            objectFit: 'cover',
            bgcolor: colors.secondary[50],
          }}
        />
        <Box sx={{ minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>Next Up:</Typography>
          <Typography sx={{
            fontWeight: 700, fontSize: 24, mt: '2px', lineHeight: 1.25,
          }}
          >
            {guide ? guide.header : `You’ve reviewed every guide for ${petName} 🎉`}
          </Typography>
          <Typography sx={{
            fontSize: 12, color: 'text.secondary', mt: { xs: 2.5, lg: 1.5 }, lineHeight: 1.4,
          }}
          >
            {guide ? guide.summary : 'Your trainer is still here if anything new comes up.'}
          </Typography>
          {guide && (
            <Box sx={{ mt: { xs: 2.5, lg: 1.5 } }}>
              <Button
                variant="contained"
                onClick={() => {
                  track('next_up_guide_click', { guide_name: guide.title });
                  onGo();
                }}
                endIcon={<ArrowDownwardIcon sx={{ fontSize: 18 }} />}
                sx={{ width: { xs: '100%', lg: 'auto' } }}
              >
                Go To Guide
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{
        flexShrink: 0,
        width: { xs: '100%', lg: 'auto' },
        maxWidth: { xs: 400, lg: 'none' },
        minWidth: { xs: 'auto', lg: 280 },
      }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.5,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
        >
          <Typography sx={{
            fontWeight: 700, fontSize: 30, color: colors.primary.main, lineHeight: 1.2,
          }}
          >
            {`${percent}%`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {`${done} of ${total} Guides Reviewed`}
          </Typography>
        </Box>
        <Box sx={{
          height: 7, borderRadius: '4px', bgcolor: colors.primary[50], mt: 1, overflow: 'hidden',
        }}
        >
          <Box sx={{
            height: '100%', width: `${percent}%`, borderRadius: '4px', bgcolor: colors.primary.main, transition: 'width 500ms ease',
          }}
          />
        </Box>
        <Typography sx={{
          fontSize: 12, color: 'text.secondary', mt: 1, textAlign: 'left',
        }}
        >
          {`Resources for ${petName}`}
        </Typography>
      </Box>
    </Box>
  );
}

export default NextUpGuide;
